import Button, { type ButtonProps } from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { type SxProps, type Theme } from '@mui/material/styles';
import { cn } from 'cn';
import { useRef, useState } from 'react';

import { Icon } from '@vestigas/ui/atoms';
import { Spinner } from '@vestigas/ui/molecules';

/**
 * Configuration for a single button option in the split button.
 */
export type ButtonOption = {
  /** The text label displayed on the button/menu item */
  /** @deprecated Use text instead */
  label?: string;
  /** The text label displayed on the button/menu item */
  text: string | undefined;
  /** Callback function executed when the option is clicked */
  onClick: () => void;
  /**
   * Whether the option is disabled (grayed out and not clickable).
   * For the primary button: combined with buttonProps.disabled using OR logic.
   * For menu items: applied individually to each MenuItem.
   */
  disabled?: boolean;
  /**
   * Whether the option is in a loading state.
   * For the primary button: shows a spinner and disables the button.
   * For menu items: shows a spinner inline and disables the item.
   */
  isLoading?: boolean;
  /**
   * Custom styling overrides for the menu item.
   * Only applies when the option is displayed in the dropdown menu.
   */
  styleOverrides?: {
    /** Custom text color (e.g., '#1e40af' for blue) */
    color?: string;
    /** Custom font weight (e.g., 600 for bold) */
    fontWeight?: number;
  };
};

/**
 * Configuration object for the split button behavior.
 *
 * @example
 * // Single button (no dropdown)
 * const config = {
 *   primary: { label: 'Save', onClick: handleSave }
 * };
 *
 * @example
 * // Split button with dropdown
 * const config = {
 *   primary: { label: 'Save', onClick: handleSave },
 *   options: [
 *     { label: 'Save', onClick: handleSave },
 *     { label: 'Save As...', onClick: handleSaveAs }
 *   ]
 * };
 */
export type ButtonConfig = {
  /** The primary button action. Always displayed as the main button. */
  primary: ButtonOption;
  /**
   * Optional array of additional options.
   * - If undefined or empty: renders a single button with no dropdown
   * - If provided: renders a split button with dropdown arrow showing all options
   */
  options?: ButtonOption[];
};

export type ButtonSplitProps = {
  /** Configuration object defining the button behavior and options */
  readonly config: ButtonConfig;
  /** Test ID for the primary button (for automated testing) */
  readonly dataTestId?: string;
  /** Icon displayed at the start of the primary button */
  readonly startIcon?: React.ReactNode;
  /**
   * Button variant style
   * @default 'contained'
   */
  readonly variant?: 'contained' | 'outlined' | 'text';
  /**
   * Additional props passed to both primary and dropdown buttons.
   * Useful for: disabled, size, aria attributes, etc.
   */
  readonly buttonProps?: ButtonProps;
  /**
   * CSS class name applied to the main button.
   */
  readonly className?: string;
  /**
   * Custom Material-UI sx prop for styling individual menu items.
   * Applied to all MenuItem components in the dropdown.
   * Individual styleOverrides from ButtonOption take precedence.
   */
  readonly menuItemSx?: SxProps<Theme>;
  /**
   * Custom Material-UI sx prop for styling the dropdown menu container (Paper).
   * Use for: shadow, border, borderRadius, padding, etc.
   */
  readonly menuPaperSx?: SxProps<Theme>;
  /**
   * Override the onClick function for the dropdown arrow button.
   * Example: used on the dln list to open the pdf download modal directly instead of opening the dropdown menu
   */
  readonly onClickShowOptions?: () => void;
};

/**
 * A flexible split button component that can render as either a single button
 * or a button with a dropdown menu of additional options.
 *
 * **Single Button Mode:**
 * When `config.options` is undefined or empty, renders a single button with no dropdown.
 *
 * **Split Button Mode:**
 * When `config.options` is provided, renders:
 * - A primary button (executes first action on click)
 * - A dropdown arrow button (shows menu with all options)
 *
 * @example
 * // Basic single button
 * <ButtonSplit
 *   config={{
 *     primary: { label: 'Save', onClick: handleSave }
 *   }}
 * />
 *
 * @example
 * // Split button with dropdown
 * <ButtonSplit
 *   config={{
 *     primary: { label: 'Export PDF', onClick: handleExportPdf },
 *     options: [
 *       { label: 'Export PDF', onClick: handleExportPdf },
 *       { label: 'Export DOCX', onClick: handleExportDocx }
 *     ]
 *   }}
 *   startIcon={<PdfIcon />}
 * />
 *
 *
 * @example
 * // Custom styled menu with styleOverrides
 * <ButtonSplit
 *   config={{
 *     primary: { label: 'Original PDF', onClick: handleOriginal },
 *     options: [
 *       {
 *         label: 'Original PDF',
 *         onClick: handleOriginal,
 *         styleOverrides: { color: '#1e40af', fontWeight: 600 }
 *       },
 *       {
 *         label: 'More options',
 *         onClick: handleMore,
 *         styleOverrides: { color: '#6b7280', fontWeight: 400 }
 *       }
 *     ]
 *   }}
 *   menuItemSx={{ px: 3, py: 1.5 }}
 *   menuPaperSx={{ borderRadius: 2, boxShadow: '...' }}
 * />
 */
export const ButtonSplit = ({
  buttonProps,
  className,
  config,
  dataTestId,
  onClickShowOptions,
  menuItemSx,
  menuPaperSx,
  startIcon,
  variant = 'contained',
}: ButtonSplitProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((previousOpen) => !previousOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const hasOptions = config.options && config.options.length > 0;
  const options = hasOptions ? config.options! : [];

  const handleOptionClick = (option: ButtonOption) => {
    option.onClick();
    setOpen(false);
  };

  const {
    disabled,
    className: classNameProp,
    ...buttonPropsRest
  } = buttonProps ?? {};

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        aria-label={
          (config.primary.text || config.primary.label) ??
          'Split button group with additional options'
        }
        variant={variant}
      >
        <Button
          className={cn(className, classNameProp)}
          data-testid={dataTestId}
          disabled={
            config.primary.disabled || config.primary.isLoading || disabled
          }
          startIcon={config.primary.isLoading ? <Spinner /> : startIcon}
          onClick={config.primary.onClick}
          {...buttonPropsRest}
        >
          {config.primary.text || config.primary.label}
        </Button>
        {hasOptions ? (
          <Button
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu"
            aria-label="select additional options"
            className={classNameProp}
            size="small"
            onClick={onClickShowOptions ?? handleToggle}
            disabled={
              disabled || config.primary.disabled || config.primary.isLoading
            }
            {...buttonPropsRest}
          >
            <Icon metaphor="expandSmall" />
          </Button>
        ) : null}
      </ButtonGroup>
      {hasOptions ? (
        <Popper
          anchorEl={anchorRef.current}
          open={open}
          role={undefined}
          sx={{ zIndex: 999 }}
          disablePortal
          transition
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper sx={menuPaperSx}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    id="split-button-menu"
                    autoFocusItem
                    disablePadding
                    className="min-w-40"
                  >
                    {options.map((option) => (
                      <MenuItem
                        key={option.text || option.label}
                        // Disable linting error because in this case we NEED the OR logic of boolean values

                        disabled={option.disabled || option.isLoading}
                        onClick={() => {
                          handleOptionClick(option);
                        }}
                        sx={{
                          ...menuItemSx,
                          color: option.styleOverrides?.color,
                          fontWeight: option.styleOverrides?.fontWeight,
                        }}
                      >
                        <div className="flex w-full items-center gap-2">
                          {option.isLoading ? <Spinner /> : null}
                          {option.text || option.label}
                        </div>
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      ) : null}
    </>
  );
};
